import React from 'react';
import CropIconMapping from 'components/CropIconMapping';
import FieldIcon from 'components/Icons/FieldIcon';
import { getHeaderSortClassName } from 'helpers/tableSort';
import cleanGeometry from 'helpers/cleanGeometry';
import {
  COLUMN_CONFIG,
  FIELD_ICON_CONFIG,
  CROP_ICON_SIZE,
  COLORS,
  FONT_SIZES
} from '../constants';
import { ApplicationPropertyVO } from '../hooks/useApplicationProperties';

export interface AppliedAreaColumnWidths {
  cropZone?: number;
  crop?: number;
  farm?: number;
  appliedArea?: number;
}

interface GetAppliedAreaTableColumnsParams {
  sortedDataLength: number;
  sortBy: string;
  sortDir: 'ascend' | 'descend';
  columnWidths?: AppliedAreaColumnWidths;
}

const getAppliedAreaTableColumns = ({
  sortedDataLength,
  sortBy,
  sortDir,
  columnWidths = {}
}: GetAppliedAreaTableColumnsParams) => [
  {
    title: `${COLUMN_CONFIG.cropZone.title} (${sortedDataLength})`,
    dataIndex: COLUMN_CONFIG.cropZone.dataIndex,
    key: COLUMN_CONFIG.cropZone.key,
    fixed: 'left' as const,
    sorter: true,
    sortOrder: sortBy === COLUMN_CONFIG.cropZone.key ? sortDir : undefined,
    width: columnWidths.cropZone ?? COLUMN_CONFIG.cropZone.width,
    className: getHeaderSortClassName(
      COLUMN_CONFIG.cropZone.key,
      sortBy,
      sortDir
    ),
    render: (value: string, record: ApplicationPropertyVO) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ flexShrink: 0 }}>
          <FieldIcon
            id={record.fieldId}
            features={[
              {
                type: 'Feature',
                geometry: cleanGeometry(record.cropZoneGeometry),
                properties: {}
              }
            ]}
            border={FIELD_ICON_CONFIG.border}
            color={FIELD_ICON_CONFIG.color}
            width={FIELD_ICON_CONFIG.width}
            height={FIELD_ICON_CONFIG.height}
          />
        </div>
        <div
          style={{
            flex: 1
          }}
        >
          <div
            style={{
              fontSize: FONT_SIZES.fieldName,
              fontStyle: 'normal',
              fontWeight: 400,
              color: COLORS.fieldText,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 1,
                WebkitBoxOrient: 'vertical',
                wordBreak: 'break-all',
                flex: record.fieldArchived ? '0 1 auto' : '1'
              }}
              title={record.fieldName}
            >
              {record.fieldName || '-'}
            </span>
            {record.fieldArchived && (
              <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                (Archived)
              </span>
            )}
          </div>
          <div
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-all'
            }}
            title={value}
          >
            {value || '-'}
          </div>
        </div>
      </div>
    )
  },
  {
    title: COLUMN_CONFIG.crop.title,
    dataIndex: COLUMN_CONFIG.crop.dataIndex,
    key: COLUMN_CONFIG.crop.key,
    sorter: true,
    sortOrder: sortBy === COLUMN_CONFIG.crop.key ? sortDir : undefined,
    width: columnWidths.crop ?? COLUMN_CONFIG.crop.width,
    className: getHeaderSortClassName(COLUMN_CONFIG.crop.key, sortBy, sortDir),
    render: (value: string) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {value ? (
          <div style={{ flexShrink: 0 }}>
            <CropIconMapping
              cropObject={{ name: value }}
              size={CROP_ICON_SIZE}
            />
          </div>
        ) : null}
        <div
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical'
          }}
          title={value}
        >
          {value || '-'}
        </div>
      </div>
    )
  },
  {
    title: COLUMN_CONFIG.farm.title,
    dataIndex: COLUMN_CONFIG.farm.dataIndex,
    key: COLUMN_CONFIG.farm.key,
    sorter: true,
    sortOrder: sortBy === COLUMN_CONFIG.farm.key ? sortDir : undefined,
    width: columnWidths.farm ?? COLUMN_CONFIG.farm.width,
    className: getHeaderSortClassName(COLUMN_CONFIG.farm.key, sortBy, sortDir),
    render: (value: string) => (
      <div
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 1,
          WebkitBoxOrient: 'vertical',
          wordBreak: 'break-all'
        }}
        title={value}
      >
        {value || '-'}
      </div>
    )
  },
  {
    title: COLUMN_CONFIG.appliedArea.title,
    dataIndex: COLUMN_CONFIG.appliedArea.dataIndex,
    key: COLUMN_CONFIG.appliedArea.key,
    sorter: true,
    sortOrder: sortBy === COLUMN_CONFIG.appliedArea.key ? sortDir : undefined,
    width: columnWidths.appliedArea ?? COLUMN_CONFIG.appliedArea.width,
    className: `${getHeaderSortClassName(
      COLUMN_CONFIG.appliedArea.key,
      sortBy,
      sortDir
    )} justify-end`,
    render: (value: number, record: ApplicationPropertyVO) => (
      <div
        style={{
          textAlign: 'right',
          whiteSpace: 'nowrap'
        }}
      >
        <div style={{ whiteSpace: 'nowrap' }}>
          {value ? `${value.toFixed(2)} ac` : '-'}
        </div>
        <div
          style={{
            fontSize: FONT_SIZES.percentage,
            fontStyle: 'normal',
            fontWeight: 400,
            color: COLORS.percentageText,
            whiteSpace: 'nowrap'
          }}
        >
          {record.appliedAreaPercent
            ? `${record.appliedAreaPercent.toFixed(2)}%`
            : '-'}
        </div>
      </div>
    )
  }
];

export default getAppliedAreaTableColumns;
